import type { BaseDocument } from '../mongo/base-document.interface';
import type { VehicleAgreementSummary } from '../vehicle-allocation/vehicle-agreement';
import type { VehicleAllocationType } from '../vehicle-allocation/vehicle-allocation.interface';

/**
 * Vehicle lifecycle status.
 *
 * A vehicle is always created as DRAFT (the create flow forces `status` to DRAFT regardless of
 * the requested value); the intended final status chosen on create is stored in `requestedStatus`,
 * and the vehicle is promoted to that `status` later via PATCH (the wizard step-5 "Complete").
 */
export const VEHICLE_STATUS = {
  ACTIVE: 'ACTIVE',
  /**
   * Retired from the fleet (SWOOP-183) — sold, written off, declared a total loss or returned to its
   * lessor. Set ONLY by `POST /vehicles/:id/archive`, never by a PATCH: it is deliberately absent from
   * `assertValidStatusTransition`, like UNDER_MAINTENANCE, so the blocking rules (no open allocation,
   * not under maintenance) cannot be bypassed by editing the Identity form.
   *
   * An archived vehicle is NOT deleted: `deletedAt` stays null so the whole profile keeps reading
   * through the normal scoped paths (seven tabs, documents, photos, change log) and Restore is an
   * ordinary update. This deviates from the `hetivo-persistence` skill's `deletedAt` archive pattern,
   * which is written for master-list entities that have no browsable profile — see the SWOOP-183 PR.
   */
  ARCHIVED: 'ARCHIVED',
  DRAFT: 'DRAFT',
  INACTIVE: 'INACTIVE',
  /**
   * Lent out as a relief vehicle against a waiting leasing agreement (SWOOP-399) — the vehicle is with
   * a customer while its ordered vehicle is late. System-owned: set ONLY by
   * `VehicleService.markOnRelief` and cleared ONLY by `VehicleService.releaseFromRelief`, each a
   * status-guarded compare-and-set, never by a PATCH. Like ARCHIVED and UNDER_MAINTENANCE it is
   * deliberately absent from the allowed `assertValidStatusTransition` moves, so the blocking rules
   * (no archive/disposal, no allocation, no work order while lent) cannot be bypassed by editing the
   * Identity form; unlike ARCHIVED, a move INTO it is refused after the no-op rule, so an ON_RELIEF
   * vehicle stays editable and its Identity save (which echoes the unchanged status) still passes.
   */
  ON_RELIEF: 'ON_RELIEF',
  // A vehicle temporarily out of operation (e.g. in the workshop). Backs the SWOOP-100 "In Service"
  // tab / KPI card and the status quick-filter option.
  UNDER_MAINTENANCE: 'UNDER_MAINTENANCE',
} as const;

export type VehicleStatus = (typeof VEHICLE_STATUS)[keyof typeof VEHICLE_STATUS];

/**
 * Allocation filter for the vehicles list (SWOOP-223) — the Available / Allocated tabs.
 *
 * Orthogonal to {@link VEHICLE_STATUS}: it filters ONLY on whether the vehicle has a currently-open
 * allocation, never on its lifecycle status. The Available TAB combines it with a status filter
 * (`{ allocationStatus: 'available', statuses: [ACTIVE] }`) so it stays structurally consistent with
 * the `available` KPI (ACTIVE and not allocated); the Allocated tab sends `allocationStatus: 'allocated'`
 * alone (any status). "Currently open" excludes future-dated allocations — see {@link VehicleListItem}.
 */
export const VEHICLE_ALLOCATION_STATUS = {
  /** The vehicle HAS a currently-open allocation. */
  ALLOCATED: 'allocated',
  /** The vehicle has NO currently-open allocation. */
  AVAILABLE: 'available',
} as const;

export type VehicleAllocationStatus = (typeof VEHICLE_ALLOCATION_STATUS)[keyof typeof VEHICLE_ALLOCATION_STATUS];

/**
 * Vehicle transmission type.
 *
 * Stored as a stable code string on the vehicle document; translated on the frontend via i18n.
 */
export const VEHICLE_TRANSMISSION = {
  AUTOMATIC: 'AUTOMATIC',
  CVT: 'CVT',
  MANUAL: 'MANUAL',
} as const;

export type VehicleTransmission = (typeof VEHICLE_TRANSMISSION)[keyof typeof VEHICLE_TRANSMISSION];

/**
 * How a vehicle was acquired (SWOOP-146 Acquisition section).
 *
 * Stored as a stable code string on the vehicle document; translated on the frontend via i18n
 * (mirrors {@link VEHICLE_TRANSMISSION}). Optional — set from the Add-Vehicle Acquisition step.
 */
export const VEHICLE_ACQUISITION_TYPE = {
  BUYBACK: 'BUYBACK',
  LEASE: 'LEASE',
  LOAN_CREDIT_PURCHASE: 'LOAN_CREDIT_PURCHASE',
  PURCHASE: 'PURCHASE',
  SUBLEASE: 'SUBLEASE',
} as const;

export type VehicleAcquisitionType = (typeof VEHICLE_ACQUISITION_TYPE)[keyof typeof VEHICLE_ACQUISITION_TYPE];

/**
 * What happens to a leased vehicle when its lease contract ends (SWOOP-238).
 *
 * Stored as a stable code string on the vehicle document; translated on the frontend via i18n
 * (mirrors {@link VEHICLE_TRANSMISSION} / {@link VEHICLE_ACQUISITION_TYPE}). Optional — one of the
 * four non-mandatory lease fields.
 */
export const VEHICLE_END_OF_LEASE_OPTION = {
  EXTEND_CONTRACT: 'EXTEND_CONTRACT',
  PURCHASE_OPTION: 'PURCHASE_OPTION',
  RETURN_TO_LESSOR: 'RETURN_TO_LESSOR',
} as const;

export type VehicleEndOfLeaseOption = (typeof VEHICLE_END_OF_LEASE_OPTION)[keyof typeof VEHICLE_END_OF_LEASE_OPTION];

/**
 * Why a vehicle left the fleet (SWOOP-183). Chosen in step 1 of the Archive drawer; each reason has its
 * own mandatory field set, and switching reason clears the values entered for the previous one.
 *
 * Stored as a stable code string on the vehicle document and translated on the frontend via i18n
 * (mirrors {@link VEHICLE_ACQUISITION_TYPE} / {@link VEHICLE_END_OF_LEASE_OPTION}). Present exactly
 * when {@link Vehicle.status} is ARCHIVED — cleared on restore, alongside every `archive*` field.
 */
export const VEHICLE_RETIREMENT_REASON = {
  RENTAL_RETURN: 'RENTAL_RETURN',
  SOLD: 'SOLD',
  TOTAL_LOSS: 'TOTAL_LOSS',
  WRITE_OFF: 'WRITE_OFF',
} as const;

export type VehicleRetirementReason = (typeof VEHICLE_RETIREMENT_REASON)[keyof typeof VEHICLE_RETIREMENT_REASON];

/**
 * Discrete energy / fuel tank level, expressed as a percentage.
 *
 * This is NOT a vehicle-document field: it is the set of allowed values for an `ENERGY_LEVEL`
 * vehicle-history entry (and the matching frontend select). The wizard records a baseline level
 * (e.g. "Enter the fuel tank level") into the vehicle-history time-series.
 */
export const VEHICLE_ENERGY_LEVEL = {
  EMPTY: 0,
  FULL: 100,
  HALF: 50,
  QUARTER: 25,
  THREE_QUARTERS: 75,
} as const;

export type VehicleEnergyLevel = (typeof VEHICLE_ENERGY_LEVEL)[keyof typeof VEHICLE_ENERGY_LEVEL];

/**
 * Full Vehicle entity as stored in MongoDB.
 *
 * A Vehicle is a tenant-scoped fleet asset. Master-data references are stored as the referenced
 * row's `id` (make / model / vehicleType / vehicleGroup / energySource / vehicleOwnership) — the
 * derived business `code` having been removed (SWOOP-127). The model can have several same-named
 * variants (trim/generation), so the vehicle references the exact Vehicle Model row by `modelId`;
 * `modelCode` denormalizes that row's model NAME (server-derived) for display/search. Every id
 * reference is persisted as a real Mongo ObjectId (the repository lists them all as refFields); the
 * domain type stays `string` (hex) because the base repository casts ObjectId↔string on read/write.
 * `locationId`, `divisionId` and `poolTypeId` are tenant-scoped FKs (the division is an embedded
 * sub-document `_id` inside the referenced Location; `poolTypeId` references a Pool Types row):
 * `divisionId` is OPTIONAL, `poolTypeId` is REQUIRED (SWOOP-148). `vehicleOptions` is an array of
 * `vehicle-available-options` row ids (also ObjectId), defaulting to an empty array.
 */
export interface Vehicle extends BaseDocument {
  /** Date the vehicle was acquired (SWOOP-146). Optional at registration; set from the Acquisition step. */
  acquisitionDate?: Date;
  /** How the vehicle was acquired (SWOOP-146). Optional; see {@link VEHICLE_ACQUISITION_TYPE}. */
  acquisitionType?: VehicleAcquisitionType;
  /** Lease contract reference the vehicle was returned under (SWOOP-183). RENTAL_RETURN only. */
  archiveContractReference?: string | null;
  /**
   * The buyer (SWOOP-183) — a `customers` row `_id`, restricted to ACTIVE customers of the vehicle's
   * territory. SOLD only. Persisted as an ObjectId (registered as a repository refField); the domain
   * type stays `string` (hex), like {@link Vehicle.lessorSupplierId}.
   */
  archiveCustomerId?: string | null;
  /** When the vehicle was archived (SWOOP-183) — server-set, not user-entered. */
  archivedAt?: Date | null;
  /**
   * The status the vehicle held immediately before it was archived (SWOOP-183) — server-set.
   *
   * Restore reads it so an archived record comes back as what it was. US10 says a restored vehicle
   * "becomes Inactive", which is right for a vehicle that was in service; but hardcoding INACTIVE also
   * let a DRAFT be archived and restored straight into the operable fleet, skipping the mandatory-lease
   * check that guards leaving DRAFT. Remembering the origin fixes that without forbidding a fleet
   * manager from archiving a vehicle they started adding by mistake — which, with no delete route for a
   * vehicle, is their only way to clear it.
   */
  archivedFromStatus?: VehicleStatus | null;
  /** Date of the incident that wrote the vehicle off (SWOOP-183). TOTAL_LOSS only. */
  archiveIncidentDate?: Date | null;
  /** Insurer's claim reference (SWOOP-183). TOTAL_LOSS only. */
  archiveInsuranceClaimReference?: string | null;
  /** When cover was suspended for this vehicle (SWOOP-183). SOLD only. */
  archiveInsuranceSuspensionDate?: Date | null;
  /** Sale invoice reference (SWOOP-183). SOLD only. */
  archiveInvoiceReference?: string | null;
  /** Why the vehicle is being written off (SWOOP-183), free text. WRITE_OFF only. */
  archiveJustification?: string | null;
  /** Odometer at disposal, in km (SWOOP-183). Always entered manually; not required for SOLD. */
  archiveOdometerReading?: number | null;
  /** When the vehicle was physically handed to the buyer (SWOOP-183) — may differ from the sale date. */
  archivePhysicalDeliveryDate?: Date | null;
  /**
   * Why the vehicle left the fleet (SWOOP-183); see {@link VEHICLE_RETIREMENT_REASON}. Set exactly when
   * {@link Vehicle.status} is ARCHIVED, and the discriminator for which other `archive*` fields are populated.
   *
   * ── The archive field set ──
   * Eighteen FLAT fields rather than one embedded `archive` sub-document, for the same reason the lease
   * fields are flat (see {@link Vehicle.lessorSupplierId}): the change-log config is keyed by schema PATH,
   * so a sub-schema collapses the whole archive into ONE entry. SWOOP-183 US5 requires the archiving
   * action *and its values* in the change log, which a collapsed entry cannot express.
   *
   * Restore CLEARS every one of them (explicit `null`, `$set` by the base repository), so each may read
   * back as `null` — the same clearing contract as the lease fields. The cleared values stay recoverable
   * from `vehicles_events`, which is what makes clearing acceptable.
   */
  archiveReason?: VehicleRetirementReason | null;
  /** Date the vehicle went back to its lessor (SWOOP-183). RENTAL_RETURN only. */
  archiveReturnDate?: Date | null;
  /**
   * Who the vehicle was returned to (SWOOP-183) — a `suppliers` row `_id`. RENTAL_RETURN only, and
   * prefilled from {@link Vehicle.lessorSupplierId} when the vehicle is leased with an end-of-lease
   * option of RETURN_TO_LESSOR. Persisted as an ObjectId (repository refField); domain type stays hex.
   */
  archiveReturnedToSupplierId?: string | null;
  /** Sale date (SWOOP-183). SOLD only; may differ from {@link Vehicle.archivePhysicalDeliveryDate}. */
  archiveSaleDate?: Date | null;
  /** Sale price in the territory currency (SWOOP-183). SOLD only. Counts as disposal proceeds. */
  archiveSalePrice?: number | null;
  /** Insurer's settlement amount (SWOOP-183). TOTAL_LOSS only. Counts as disposal proceeds. */
  archiveSettlementAmount?: number | null;
  /** Date the vehicle was written off the asset register (SWOOP-183). WRITE_OFF only. */
  archiveWriteOffDate?: Date | null;
  /** Write-off reference (SWOOP-183). WRITE_OFF only. */
  archiveWriteOffReference?: string | null;
  /** CO₂ emissions in grams per kilometre (g/km). Optional one-time specification. */
  co2Emissions?: number;
  /** Free-text colour. Optional one-time specification. */
  colour?: string;
  /**
   * Contract mileage allowance in km per YEAR (SWOOP-238). Optional lease field. Reads back as `null`
   * once cleared — see the block comment on {@link Vehicle.lessorSupplierId}.
   */
  contractMileageLimit?: number | null;
  /**
   * Embedded division `_id` within the referenced location. OPTIONAL (SWOOP-148). May read back as
   * `null` when it was explicitly cleared via a PATCH `{ divisionId: null }` — see the clearing
   * behaviour on {@link UpdateVehicle.divisionId}.
   */
  divisionId?: string | null;
  /**
   * Electric consumption in kWh per 100 km. Optional; only relevant for electric/hybrid sources.
   * May read back as `null` when it was explicitly cleared (the energy source changed to a
   * non-electric one) — see the clearing behaviour on {@link UpdateVehicle.electricConsumption}.
   */
  electricConsumption?: number | null;
  /**
   * What happens when the lease contract ends (SWOOP-238). Optional lease field; see
   * {@link VEHICLE_END_OF_LEASE_OPTION}. Reads back as `null` once cleared.
   */
  endOfLeaseOption?: VehicleEndOfLeaseOption | null;
  /** Engine power in kilowatts (kW). Manual input, optional one-time specification. */
  enginePowerKw?: number;
  /** Engine displacement in litres (canonical value — the unit "L" is UI-only, not stored). */
  engineSize?: number;
  energySourceId: string;
  engineId?: string;
  /**
   * Charge per km driven beyond {@link Vehicle.contractMileageLimit}, in the territory currency
   * (SWOOP-238). Optional lease field. Reads back as `null` once cleared.
   */
  excessMileageCharge?: number | null;
  /** First date the vehicle was registered (SWOOP-146). Optional. */
  firstRegistrationDate?: Date;
  /** First-year fees, in the territory currency (SWOOP-146). Optional. */
  firstYearFees?: number;
  /** Fiscal horsepower (tax-rating HP). Manual input, optional one-time specification. */
  fiscalHorsepower?: number;
  /** Fuel consumption in litres per 100 km (L/100km). Optional one-time specification. */
  fuelConsumption?: number;
  /**
   * The lessor's contract reference (SWOOP-238). MANDATORY whenever the vehicle's ownership row carries
   * the `leased` code — see the block comment on {@link Vehicle.lessorSupplierId}. Free text, not an id.
   */
  leaseContractReference?: string | null;
  /** Lease contract end date (SWOOP-238). Mandatory while leased; must be AFTER `leaseStartDate`. */
  leaseEndDate?: Date | null;
  /** Lease contract start date (SWOOP-238). Mandatory while leased. */
  leaseStartDate?: Date | null;
  /**
   * The company the vehicle is leased FROM (SWOOP-238): a `suppliers` row `_id` whose `type` is
   * `LESSOR`. Optional relation — persisted as an ObjectId (registered as a repository refField); the
   * domain type stays `string` (hex), like {@link Vehicle.vendorSupplierId}.
   *
   * ── The lease field set ──
   * Eight FLAT fields (this one, `leaseContractReference`, `leaseStartDate`, `leaseEndDate`,
   * `monthlyLeaseCost`, `contractMileageLimit`, `excessMileageCharge`, `endOfLeaseOption`) rather than
   * one embedded sub-document, so each one is an independent change-log entry with its own old/new
   * values (the change-log config is keyed by schema PATH, and a sub-schema would collapse to one).
   *
   * They apply only while the vehicle's ownership row carries the `leased` code
   * ({@link VEHICLE_OWNERSHIP_CODE_LEASED}); the first four are then mandatory and the promotion of a
   * DRAFT vehicle is refused without them. Switching ownership away from leased CLEARS all eight, so
   * every one of them may read back as `null` (an omitted PATCH field is left unchanged; an explicit
   * `null` is `$set` — the same clearing contract as {@link Vehicle.divisionId}).
   */
  lessorSupplierId?: string | null;
  licensePlate: string;
  locationId: string;
  makeId: string;
  modelCode: string;
  modelId: string;
  /**
   * Recurring monthly lease cost, in the territory currency (SWOOP-238). Optional lease field. Reads
   * back as `null` once cleared.
   */
  monthlyLeaseCost?: number | null;
  /** Number of doors. Optional one-time specification. */
  numberOfDoors?: number;
  /** Number of seats. Optional one-time specification. */
  numberOfSeats?: number;
  poolTypeId: string;
  /** Purchase or lease cost, in the territory currency (SWOOP-146). Optional. */
  purchaseOrLeaseCost?: number;
  registrationExpiryDate?: Date;
  requestedStatus: VehicleStatus;
  /** Set when two or more plans tie for this vehicle (SWOOP-307): their ids, so the profile can say "Plan conflict" and name them. Empty otherwise. System-written. */
  servicePlanConflictIds: string[];
  /** The vehicle's winning service plan (SWOOP-307): one id for now; the array only leaves room for more later. Empty when no plan applies or two plans tie. System-written — never accepted from clients. */
  servicePlanIds: string[];
  status: VehicleStatus;
  /** Transmission type (MANUAL / AUTOMATIC / CVT). Optional one-time specification. */
  transmission?: VehicleTransmission;
  vehicleGroupId: string;
  vehicleId: string;
  /** Selected vehicle options: array of `vehicle-available-options` row `_id`s. Defaults to an empty array. */
  vehicleOptions: string[];
  /** Vehicle ownership reference (tenant-scoped Vehicle Ownerships list `_id`). Optional one-time specification. */
  vehicleOwnershipId?: string;
  vehicleTypeId: string;
  /**
   * Vendor / supplier the vehicle was acquired from (SWOOP-146): a `suppliers` row `_id` whose
   * `type` is `VEHICLE_DEALER`. Optional relation — persisted as an ObjectId (registered as a
   * repository refField); the domain type stays `string` (hex).
   */
  vendorSupplierId?: string;
  vin: string;
  /** Warranty expiry date (SWOOP-146). Optional; will drive a future renewal reminder. */
  warrantyExpiryDate?: Date;
  /** Warranty expiry mileage in km (SWOOP-146). Optional. */
  warrantyExpiryMileage?: number;
  year: number;
}

/**
 * Payload for creating a new Vehicle.
 * Omits BaseDocument fields. `requestedStatus` is NOT supplied by the caller: it is derived by the
 * service from `status` (the intended final status), while the persisted `status` is forced to DRAFT.
 * `modelCode` is NOT supplied by the caller either: the client sends `modelId` (the exact Vehicle
 * Model row) and the service derives/denormalizes `modelCode` (the model name) from it.
 * `vehicleId` is optional: when absent it is auto-generated as the highest purely-numeric `vehicleId`
 * ever used for the tenant (including soft-deleted tombstones) + 1, derived straight from the
 * vehicles collection rather than a separate counter; when supplied it is used verbatim (manual override).
 */
export type CreateVehicle = Omit<
  Vehicle,
  | 'createdAt'
  | 'deletedAt'
  | 'id'
  | 'modelCode'
  | 'requestedStatus'
  | 'servicePlanConflictIds'
  | 'servicePlanIds'
  | 'tenantId'
  | 'updatedAt'
  | 'vehicleId'
> & {
  vehicleId?: string;
};

/**
 * Payload for updating an existing Vehicle via PATCH.
 * As with create, the caller supplies `modelId`; the service derives/denormalizes `modelCode`
 * (the model name) from it, so `modelCode` here is server-internal (not sent by clients).
 *
 * `requestedStatus` is editable on PATCH: it is the intended final status while the vehicle stays
 * DRAFT (the value the wizard step-5 "Complete" promotes the vehicle to), so the Add-Vehicle wizard
 * can revise it on an existing DRAFT vehicle. `status` is a GUARDED promotion-only field: the service
 * only allows promoting a DRAFT vehicle to its own `requestedStatus`, never an arbitrary transition.
 */
/**
 * A vehicle enriched with its latest time-series readings for the vehicles list (SWOOP-100).
 *
 * `latestOdometer` (km) and `latestEnergyLevel` (percentage) are derived server-side from the most
 * recent `vehicle-history` entry of each type, or `null` when the vehicle has no such reading yet.
 * `renewingInsuranceDate` is the vehicle's next renewal deadline (a `Date`, matching the
 * `registrationExpiryDate` convention): the SOONEST UPCOMING `vehicle-insurance` end date (earliest not
 * yet lapsed), falling back to the most recent past end date when every policy has lapsed, or `null`
 * when the vehicle has no policy at all (SWOOP-145). They are read-only projections — never persisted on
 * the vehicle document.
 *
 * `currentAllocation` is the vehicle's CURRENTLY-OPEN allocation (SWOOP-223), derived server-side in the
 * list aggregation from the `vehicle-allocations` collection + the driver/user join — the holder's
 * resolved name, the allocation type, and the period. `null` when the vehicle has no open allocation right now.
 * Like the readings above it is a read-only projection, NEVER persisted on the vehicle document (there is
 * deliberately no `currentDriverId` foreign key on the vehicle). "Currently open" means a live allocation
 * whose `startAt` has passed and whose `endAt` is null or still in the future — a FUTURE-dated allocation
 * is therefore NOT reflected here (the vehicle stays "Not allocated" in the list until it starts).
 */
/**
 * An allocation as projected onto a vehicles-list row: the resolved holder name, the allocation type and the
 * period. Shared by {@link VehicleListItem.currentAllocation} and
 * {@link VehicleListItem.upcomingAllocation} — the two differ only in WHICH allocation they select, not
 * in shape.
 */
export interface VehicleListItemAllocation {
  allocationType: VehicleAllocationType;
  endAt: Date | null;
  holderName: string;
  startAt: Date;
}

export interface VehicleListItem extends Vehicle {
  /**
   * The LIVE agreement a vehicle is on (SWOOP-422/423), or null for a vehicle on no live agreement. The
   * Allocation column shows it in preference to the driver allocation (FR-011): an ASSIGNED vehicle (no
   * allocation yet), an individual's ON_LEASE vehicle (no driver row) and a company's ON_LEASE vehicle (the
   * customer, not the driver). Such a vehicle counts as ALLOCATED in the KPIs and the Available / Allocated
   * tabs, and the list refuses Allocate, Change and Archive on it. Replaces the former `leasedToCustomerName`.
   */
  currentAgreement: VehicleAgreementSummary | null;
  currentAllocation: VehicleListItemAllocation | null;
  latestEnergyLevel: number | null;
  renewingInsuranceDate: Date | null;
  latestOdometer: number | null;
  /**
   * The vehicle's soonest allocation that has NOT started yet — `startAt` in the future, and not
   * already closed (SWOOP-238 follow-up). `null` when none exists.
   *
   * Exists because `currentAllocation` deliberately excludes a future-dated allocation, while the
   * vehicle profile's allocation card shows one as soon as it is created. Without this the list could
   * only say "Not allocated" for a vehicle the profile showed as allocated — the same allocation,
   * described two contradictory ways. The list now reports it as SCHEDULED instead.
   *
   * Deliberately a SEPARATE field rather than a relaxation of `currentAllocation`: the Available /
   * Allocated tabs and the KPI counts are defined on "currently allocated", and a vehicle booked for
   * next month is genuinely still available today. Those counts must not move.
   */
  upcomingAllocation: VehicleListItemAllocation | null;
}

/**
 * KPI counts for the vehicles list header cards (SWOOP-100 / SWOOP-223).
 *
 * The counts deliberately OVERLAP — a single vehicle can be counted in several cards — because each
 * card answers an independent question over the same non-archived population:
 *  - `available` and `allocated` partition ACTIVE-vs-allocated differently from `inService`, so
 *    `available + allocated` can exceed `total`, and an ACTIVE allocated vehicle counts in both
 *    `allocated` and `inService`. This is intended; do not "reconcile" the numbers to sum to `total`.
 */
export interface VehicleFleetStats {
  /**
   * Vehicles that are ACTIVE **and** NOT currently allocated (SWOOP-223) — the "ready to hand out"
   * count. Structurally consistent with the Available tab, which the frontend expresses as
   * `{ allocationStatus: 'available', statuses: [ACTIVE] }`.
   */
  available: number;
  /** Vehicles with a currently-open allocation, regardless of status (SWOOP-223). */
  allocated: number;
  /** Vehicles that are ACTIVE or UNDER_MAINTENANCE — the "in service" population (SWOOP-223). */
  inService: number;
  /** All non-archived vehicles for the tenant (within the caller's scope). */
  total: number;
}

export interface UpdateVehicle {
  // SWOOP-146 Acquisition fields. All optional: the vehicle is created (DRAFT) in the Identity step,
  // and acquisition data is PATCHed on later from the Acquisition step. An omitted/undefined field is
  // left unchanged by PATCH.
  acquisitionDate?: Date;
  acquisitionType?: VehicleAcquisitionType;
  co2Emissions?: number;
  colour?: string;
  // ── Lease fields (SWOOP-238) ──
  // All eight accept `null` to CLEAR the stored value, which is how switching ownership away from
  // "leased" wipes the contract in the same PATCH that changes the ownership. An omitted/undefined
  // field is left unchanged (same contract as `divisionId` / `electricConsumption` below).
  contractMileageLimit?: number | null;
  // `null` explicitly clears a previously-stored division (an undefined/omitted field is left
  // unchanged by PATCH). Division is optional since SWOOP-148, so a vehicle can be un-assigned from
  // its division; the base repository `$set`s the null, and validation skips a null division.
  divisionId?: string | null;
  // `null` explicitly clears a previously-stored value (an undefined field is left unchanged by PATCH):
  // used to wipe a stale electric consumption after the energy source changes to a non-electric one.
  electricConsumption?: number | null;
  endOfLeaseOption?: VehicleEndOfLeaseOption | null;
  enginePowerKw?: number;
  engineSize?: number;
  energySourceId?: string;
  engineId?: string;
  excessMileageCharge?: number | null;
  firstRegistrationDate?: Date;
  firstYearFees?: number;
  fiscalHorsepower?: number;
  fuelConsumption?: number;
  leaseContractReference?: string | null;
  leaseEndDate?: Date | null;
  leaseStartDate?: Date | null;
  lessorSupplierId?: string | null;
  licensePlate?: string;
  locationId?: string;
  makeId?: string;
  modelCode?: string;
  modelId?: string;
  monthlyLeaseCost?: number | null;
  numberOfDoors?: number;
  numberOfSeats?: number;
  poolTypeId?: string;
  purchaseOrLeaseCost?: number;
  registrationExpiryDate?: Date;
  requestedStatus?: VehicleStatus;
  status?: VehicleStatus;
  transmission?: VehicleTransmission;
  vehicleGroupId?: string;
  vehicleId?: string;
  vehicleOptions?: string[];
  vehicleOwnershipId?: string;
  vehicleTypeId?: string;
  vendorSupplierId?: string;
  vin?: string;
  warrantyExpiryDate?: Date;
  warrantyExpiryMileage?: number;
  year?: number;
}

/**
 * What `POST /vehicles/:id/archive` accepts (SWOOP-183).
 *
 * `archiveReason` picks the field set the service validates: every other property is optional HERE and
 * made mandatory per-reason by the service, because one body type has to serve four different forms.
 * The service rejects any field that does not belong to the chosen reason rather than silently storing
 * it, so a client cannot smuggle a sale price onto a write-off.
 *
 * Keys match the stored `archive*` field names exactly, so the HTTP body derives from `VehicleDTO` with
 * `PickType` (decorators and all) and no client key needs remapping onto a column.
 *
 * Deliberately NOT part of {@link UpdateVehicle}: archiving runs its own endpoint so the blocking rules
 * (no open allocation, not under maintenance, not already archived) cannot be bypassed via PATCH.
 * `archivedAt` is absent because the server stamps it.
 */
export interface ArchiveVehicle {
  archiveContractReference?: string;
  archiveCustomerId?: string;
  archiveIncidentDate?: Date;
  archiveInsuranceClaimReference?: string;
  archiveInsuranceSuspensionDate?: Date;
  archiveInvoiceReference?: string;
  archiveJustification?: string;
  archiveOdometerReading?: number;
  archivePhysicalDeliveryDate?: Date;
  archiveReason: VehicleRetirementReason;
  archiveReturnDate?: Date;
  archiveReturnedToSupplierId?: string;
  archiveSaleDate?: Date;
  archiveSalePrice?: number;
  archiveSettlementAmount?: number;
  archiveWriteOffDate?: Date;
  archiveWriteOffReference?: string;
}
